import type {
  PublicResearchTeamProfileResponse,
  WorldcupResearchQuery,
  WorldcupResearchTeamMatchStatRow,
} from '~/features/worldcup-research/types'
import type { ImplementedLocale } from '~/lib/i18n/shared'
import { Button, Card } from '@heroui/react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Download, LineChart } from 'lucide-react'

import * as React from 'react'
import { useLocale, useTranslations } from 'use-intl'
import { ProductBreadcrumbs } from '~/components/layout/product-breadcrumbs'
import { exportPublicResearchTeamProfileCsv } from '~/features/public-research/api'
import {
  nationalTeamProfileDefaultFilters,
  publicResearchTeamProfileQueryOptions,
} from '~/features/public-research/query-options'
import { createSessionQuery } from '~/lib/session-query'

// Shared components and helper functions from ./components
import {
  compactJson,
  downloadCsv,
  EvidenceList,
  formatValue,
  isUnauthorizedResearchError,
  KeyValue,
  PageHeader,
  ResearchStateNotice,
  SelectInput,
  SourceWarningList,
  StateText,
  SummaryMetric,
  translateOptional,
  userVisibleWarnings,
} from './components'

type PublicResearchHalfTimeLabelKey =
  | NonNullable<PublicResearchTeamProfileResponse['halfTimeRhythm']>['labelKey']
  | NonNullable<PublicResearchTeamProfileResponse['halfTimeRhythm']>['recentExamples'][number]['labelKey']

type TeamHalfTimeRhythmMetric = NonNullable<PublicResearchTeamProfileResponse['halfTimeRhythm']>['averages'][number]

function formatHalfTimeMetricValue(value: number | null, unit: 'count' | 'percent') {
  if (value === null)
    return '-'
  const rounded = Number.isInteger(value) ? String(value) : value.toFixed(1)
  return unit === 'percent' ? `${rounded}%` : rounded
}

function halfTimeMetricLabel(metric: TeamHalfTimeRhythmMetric, locale: ImplementedLocale) {
  return locale === 'zh-CN' ? metric.labelZh : metric.labelEn
}

function localizedHalfTimeLabel(t: ReturnType<typeof useTranslations>, labelKey: PublicResearchHalfTimeLabelKey) {
  return translateOptional(t, `halfTimeRhythm.labels.${labelKey}`, labelKey)
}

function TeamHalfTimeMetricTile({ metric, firstHalfLabel, secondHalfLabel }: {
  metric: TeamHalfTimeRhythmMetric
  firstHalfLabel: string
  secondHalfLabel: string
}) {
  const locale = useLocale() as ImplementedLocale
  const delta = metric.delta === null ? '-' : `${metric.delta > 0 ? '+' : ''}${formatHalfTimeMetricValue(metric.delta, metric.unit)}`
  return (
    <div className="rounded-md border border-divider bg-background/70 p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="font-bold">{halfTimeMetricLabel(metric, locale)}</div>
        <span className="rounded-md bg-default-100 px-2 py-1 text-xs font-semibold tabular-nums text-muted">
          {delta}
        </span>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
        <div className="rounded-md bg-default-50 p-2">
          <div className="text-xs font-semibold text-muted">{firstHalfLabel}</div>
          <div className="mt-1 font-black tabular-nums">{formatHalfTimeMetricValue(metric.firstHalfValue, metric.unit)}</div>
        </div>
        <div className="rounded-md bg-default-50 p-2">
          <div className="text-xs font-semibold text-muted">{secondHalfLabel}</div>
          <div className="mt-1 font-black tabular-nums">{formatHalfTimeMetricValue(metric.secondHalfValue, metric.unit)}</div>
        </div>
      </div>
    </div>
  )
}

function TeamHalfTimeRhythmPanel({ data }: { data: PublicResearchTeamProfileResponse }) {
  const t = useTranslations('ResearchWorkspace.profile')
  const rhythm = data.halfTimeRhythm
  if (!rhythm)
    return null

  if (rhythm.status !== 'available') {
    return (
      <ResearchStateNotice
        title={t('halfTimeRhythm.insufficientSampleTitle')}
        body={t('halfTimeRhythm.insufficientSampleBody', {
          usable: rhythm.sample.usableMatches,
          minimum: rhythm.sample.minimumRequired,
          total: rhythm.sample.totalMatchesConsidered,
        })}
        tone="warning"
      />
    )
  }

  return (
    <Card className="p-5" variant="secondary">
      <Card.Header>
        <div>
          <h2 className="text-lg font-bold">{t('halfTimeRhythm.title')}</h2>
          <p className="mt-1 text-sm text-muted">
            {t('halfTimeRhythm.sample', {
              usable: rhythm.sample.usableMatches,
              total: rhythm.sample.totalMatchesConsidered,
            })}
          </p>
        </div>
      </Card.Header>
      <Card.Content className="space-y-4">
        <div className="rounded-md border border-primary/20 bg-primary/5 px-3 py-2 text-sm font-semibold">
          {localizedHalfTimeLabel(t, rhythm.labelKey)}
        </div>
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {rhythm.averages.map((metric: any) => (
            <TeamHalfTimeMetricTile
              key={metric.key}
              metric={metric}
              firstHalfLabel={t('halfTimeRhythm.firstHalfAverage')}
              secondHalfLabel={t('halfTimeRhythm.secondHalfAverage')}
            />
          ))}
        </div>
        {rhythm.recentExamples.length > 0 && (
          <div>
            <h3 className="text-sm font-bold">{t('halfTimeRhythm.recentExamples')}</h3>
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              {rhythm.recentExamples.slice(0, 4).map((example: any) => (
                <Link
                  key={example.matchId}
                  to="/research/matches/$matchId"
                  params={{ matchId: example.matchId }}
                  className="rounded-md border border-divider bg-background/70 px-3 py-2 text-sm font-semibold transition hover:bg-default-50"
                >
                  <span>{example.opponent}</span>
                  <span className="mt-1 block text-xs font-medium text-muted">
                    {localizedHalfTimeLabel(t, example.labelKey)}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </Card.Content>
    </Card>
  )
}

function ResearchTrendChart({ data }: { data: Array<{ matchId: string, season: number | string, shotsOnTarget: number | null, corners: number | null }> }) {
  const t = useTranslations('ResearchWorkspace.profile')
  if (data.length === 0)
    return <StateText>{t('noTrend')}</StateText>

  const values = data.map(point => point.shotsOnTarget ?? 0)
  const max = Math.max(...values, 1)
  const points = values.map((value, index) => {
    const x = data.length === 1 ? 50 : 8 + (index * 84) / (data.length - 1)
    const y = 88 - (value / max) * 72
    return `${x},${y}`
  }).join(' ')

  return (
    <div>
      <svg data-testid="research-trend-chart" viewBox="0 0 100 100" className="h-52 w-full overflow-visible rounded-md border border-divider bg-background">
        <polyline fill="none" stroke="currentColor" strokeWidth="2.5" points={points} className="text-primary" />
        {data.map((point, index) => {
          const value = point.shotsOnTarget ?? 0
          const x = data.length === 1 ? 50 : 8 + (index * 84) / (data.length - 1)
          const y = 88 - (value / max) * 72
          return <circle key={point.matchId} cx={x} cy={y} r="2.6" className="fill-primary" />
        })}
      </svg>
      <div className="mt-3 grid gap-2 text-xs text-muted sm:grid-cols-2">
        {data.map(point => (
          <div key={point.matchId}>
            {point.season}
            :
            {' '}
            {t('trendPoint', { shots: formatValue(point.shotsOnTarget), corners: formatValue(point.corners), matches: 1 })}
          </div>
        ))}
      </div>
    </div>
  )
}

function MatchStatsTable({ rows, title, empty }: { rows: WorldcupResearchTeamMatchStatRow[], title: string, empty: string }) {
  const { data: session } = useQuery(createSessionQuery())
  const isAdmin = session?.user?.role === 'admin'
  const t = useTranslations('ResearchWorkspace.profile')
  return (
    <Card className="overflow-hidden" variant="secondary">
      <Card.Header>
        <h2 className="text-lg font-bold">{title}</h2>
      </Card.Header>
      <Card.Content className="p-0">
        {rows.length === 0 && <div className="p-5 text-sm text-muted">{empty}</div>}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-left text-sm">
            <thead className="border-b border-divider text-xs uppercase text-muted">
              <tr>
                <th className="px-4 py-3">{t('table.match')}</th>
                <th className="px-4 py-3">{t('table.competition')}</th>
                <th className="px-4 py-3">{t('table.score')}</th>
                <th className="px-4 py-3">{t('table.shots')}</th>
                <th className="px-4 py-3">{t('table.confidence')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.id} className="border-b border-divider">
                  <td className="px-4 py-3 font-semibold">
                    {row.team}
                    {' '}
                    {t('table.versus')}
                    {' '}
                    {row.opponent}
                  </td>
                  <td className="px-4 py-3">
                    {row.competitionName}
                    {' '}
                    {row.season}
                  </td>
                  <td className="px-4 py-3">
                    {formatValue(row.goals)}
                    -
                    {formatValue(row.opponentGoals)}
                  </td>
                  <td className="px-4 py-3">{t('table.statLine', { shots: formatValue(row.shotsOnTarget), total: formatValue(row.shotsTotal), corners: formatValue(row.corners) })}</td>
                  <td className="px-4 py-3">
                    {isAdmin
                      ? (
                          <details>
                            <summary className="cursor-pointer font-semibold">{t('table.fieldConfidence')}</summary>
                            <pre className="mt-2 max-w-xs overflow-auto rounded-md bg-default-100 p-2 text-xs">{compactJson(row.fieldConfidence)}</pre>
                          </details>
                        )
                      : (
                          <span className="text-muted">—</span>
                        )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card.Content>
    </Card>
  )
}

export function NationalTeamResearchProfile({ teamSlug }: { teamSlug: string }) {
  const t = useTranslations('ResearchWorkspace.profile')
  const [filters, setFilters] = React.useState<WorldcupResearchQuery>({
    ...nationalTeamProfileDefaultFilters,
  })
  const profileQuery = useQuery(publicResearchTeamProfileQueryOptions(teamSlug, filters))
  const exportMutation = useMutation({
    mutationFn: () => exportPublicResearchTeamProfileCsv(teamSlug, filters),
    onSuccess: csv => downloadCsv(`national-team-profile-${teamSlug}.csv`, csv),
  })
  const data = profileQuery.data

  return (
    <div className="space-y-5">
      <ProductBreadcrumbs
        items={[
          { label: t('breadcrumbs.research'), to: '/research' },
          { label: t('breadcrumbs.teamProfile') },
        ]}
      />
      <PageHeader
        eyebrow={t('eyebrow')}
        title={data ? t('titleWithTeam', { team: data.team.name }) : t('title')}
        subtitle={t('subtitle')}
      />
      <Card className="p-5" variant="secondary">
        <div className="grid gap-3 md:grid-cols-[1fr_1fr_auto]">
          <SelectInput
            label={t('researchViewLabel')}
            value={filters.researchView ?? 'major_tournament'}
            options={['major_tournament', 'competitive_context', 'exploratory_all']}
            onChange={researchView => setFilters(current => ({ ...current, researchView: researchView as WorldcupResearchQuery['researchView'], offset: 0 }))}
          />
          <SelectInput
            label={t('competitionCodeLabel')}
            value={filters.competitionCode ?? 'world_cup'}
            options={['world_cup', 'euro', 'copa_america', 'nations_league', 'world_cup_qualifier']}
            onChange={competitionCode => setFilters(current => ({ ...current, competitionCode: competitionCode as WorldcupResearchQuery['competitionCode'], offset: 0 }))}
          />
          <div className="flex items-end">
            <Button
              variant="outline"
              onPress={() => exportMutation.mutate()}
              isDisabled={profileQuery.isPending || exportMutation.isPending}
            >
              <Download className="size-4" />
              {t('exportCsv')}
            </Button>
          </div>
        </div>
      </Card>

      {profileQuery.isPending && <Card className="p-5 text-sm text-muted" variant="secondary">{t('loading')}</Card>}
      {profileQuery.isError && (
        <ResearchStateNotice
          title={isUnauthorizedResearchError(profileQuery.error) ? t('accessDeniedTitle') : t('errorTitle')}
          body={isUnauthorizedResearchError(profileQuery.error) ? t('accessDeniedBody') : t('error')}
          tone="danger"
        />
      )}
      {data && (
        <>
          <section className="grid gap-3 md:grid-cols-4">
            <SummaryMetric label={t('summary.matches')} value={data.summary.matches} />
            <SummaryMetric label={t('summary.record')} value={t('summary.recordValue', { wins: data.summary.wins ?? 0, draws: data.summary.draws ?? 0, losses: data.summary.losses ?? 0 })} />
            <SummaryMetric label={t('summary.avgShotsOnTarget')} value={formatValue(data.summary.avgShotsOnTarget)} />
            <SummaryMetric label={t('summary.avgCorners')} value={formatValue(data.summary.avgCorners)} />
          </section>
          {data.rows.length === 0 && (
            <ResearchStateNotice
              title={t('emptyTitle')}
              body={t('emptyWithScope', { team: data.team.name, researchView: data.appliedFilters.researchView ?? 'major_tournament' })}
            />
          )}
          <SourceWarningList title={t('sourceWarningTitle')} warnings={data.quality.warnings} />
          <TeamHalfTimeRhythmPanel data={data} />
          <section className="grid gap-4 lg:grid-cols-[0.85fr_1.15fr]">
            <Card className="p-5" variant="secondary">
              <Card.Header>
                <div className="flex items-center gap-2">
                  <LineChart className="size-5 text-primary" />
                  <h2 className="text-lg font-bold">{t('trendTitle')}</h2>
                </div>
              </Card.Header>
              <Card.Content>
                <ResearchTrendChart data={data.trend} />
              </Card.Content>
            </Card>
            <Card className="p-5" variant="secondary">
              <Card.Header>
                <h2 className="text-lg font-bold">{t('qualityTitle')}</h2>
              </Card.Header>
              <Card.Content className="space-y-4">
                <KeyValue label={t('quality.rowCount')} value={data.quality.sourceUsage.rowCount} />
                <KeyValue label={t('quality.qualityRange')} value={data.quality.dataTiers.join(', ') || '-'} />
                <EvidenceList title={t('quality.allowedUse')} values={data.allowedUse} />
                <EvidenceList title={t('quality.prohibitedUse')} values={data.prohibitedUse} />
                <EvidenceList title={t('quality.warnings')} values={userVisibleWarnings(data.quality.warnings)} />
              </Card.Content>
            </Card>
          </section>
          <MatchStatsTable rows={data.rows} title={t('matchesTitle')} empty={t('empty')} />
        </>
      )}
    </div>
  )
}
